import React, { useState, useEffect } from 'react';
import { BreadcrumbGroup, Tabs, Table, Link, SpaceBetween, Badge, Header, Box, Toggle, TextFilter, Pagination, ToggleProps, Flashbar, FlashbarProps } from '@cloudscape-design/components';
import { CustomAppLayout } from '../../layout';
import { useGlobalState } from '../../../context/GlobalStateContext';
import { formatPhone, storeJWT } from '../../../commons/functions';
import { useStoreAdmin } from '../../../context/useStoreAdmin';

interface AccessItem {
    idAcceso: number;
    acceso: string;
    idSector: number;
    sector: string;
    isMantAcceso: number;
    isActive: number;
}
interface UnitItem {
    idUnidad: number;
    unidad: string;
    isBlockUnit?: number;
}
interface UserItem {
    idControl: number;
    displayName: string;
    movil: string;
    email: string;
    isBlocked?: number;
}

export const SecurityBlocksPage = () => {
    const storeAdmin = useStoreAdmin();
    const { idPropiedad, setIdPropiedad } = useGlobalState();
    const { JWT } = useGlobalState();
    const { propiedad } = useGlobalState();
    const [accesses, setAccesses] = useState<AccessItem[]>([]);
    const [users, setUsers] = useState<UserItem[]>([]);
    const [units, setUnits] = useState<UnitItem[]>([]);

    const { setLoading } = useGlobalState();

    const [filteringTextUnit, setFilteringTextUnit] = useState('');
    const [filteringTextUsers, setFilteringTextUsers] = useState('');
    const [filteringTextAccess, setFilteringTextAccess] = useState('');

    const [currentPageIndexUnit, setCurrentPageIndexUnit] = useState(0);
    const [currentPageIndexUsers, setCurrentPageIndexUsers] = useState(0);
    const [currentPageIndexAccess, setCurrentPageIndexAccess] = useState(0);

    const [pageSizeUnit, setPageSizeUnit] = useState(20);
    const [pageSizeUsers, setPageSizeUsers] = useState(20);
    const [pageSizeAccess, setPageSizeAccess] = useState(20);

    const [activeTabId, setActiveTabId] = useState<string>('unit');


    const [messagesUnit, setMessagesUnit] = useState<FlashbarProps.MessageDefinition[]>([]);
    const [messagesAccess, setMessagesAccess] = useState<FlashbarProps.MessageDefinition[]>([]);
    const [messagesUsers, setMessagesUsers] = useState<FlashbarProps.MessageDefinition[]>([]);

    useEffect(() => {
        if (activeTabId === 'unit') {
            handleRefreshUnits();
        }
    }, []); // Add JWT and idPropiedad as dependencies

    const handleChangeUnit = async (item: UnitItem, detail: ToggleProps) => {
        setUnits(prevUnits =>
            prevUnits?.map(unit =>
                unit.idUnidad === item.idUnidad ? { ...unit, isBlockUnit: detail.checked ? 1 : 0 } : unit
            ) || []
        );
        const success = await handleUpdateUnits(item.idUnidad, detail.checked);
        if (!success) {
            setUnits(prevUnits =>
                prevUnits?.map(unit =>
                    unit.idUnidad === item.idUnidad ? { ...unit, isBlockUnit: item.isBlockUnit } : unit
                ) || []
            );
        } else {

            setMessagesUnit([
                {
                    type: !detail.checked ? "info" : "warning",
                    content: (<span>
                        Unidad <strong>{item.unidad}</strong> ha sido {detail.checked ? 'bloqueada' : 'desbloqueada'}
                    </span>),
                    dismissible: true,
                    onDismiss: () => setMessagesUnit([]),
                    id: 'unit-error',
                },
            ]);
            setTimeout(() => {
                setMessagesUnit([]);
            }, 5000);
        }
    };
    const handleChangeAccess = async (item: AccessItem, detail: ToggleProps) => {
        setAccesses(prevAccess =>
            prevAccess?.map(access =>
                access.idAcceso === item.idAcceso ? { ...access, isActive: detail.checked ? 0 : 1 } : access
            ) || []
        );
        const success = await handleUpdateAccess(item.idAcceso, detail.checked);
        if (!success) {
            setAccesses(prevAccesses =>
                prevAccesses?.map(access =>
                    access.idAcceso === item.idAcceso ? { ...access, isBlockUnit: item.isActive } : access
                ) || []
            );
        } else {

            setMessagesAccess([
                {
                    type: !detail.checked ? "info" : "warning",
                    content: (<span>
                        Acceso <strong>{item.acceso}</strong> ha sido {detail.checked ? 'bloqueado' : 'desbloqueado'}
                    </span>),
                    dismissible: true,
                    onDismiss: () => setMessagesAccess([]),
                    id: 'access-error',
                },
            ]);
            setTimeout(() => {
                setMessagesAccess([]);
            }, 5000);
        }
    };

    const handleChangeUsers = async (item: UserItem, detail: ToggleProps) => {
        setUsers(prevAccess =>
            prevAccess?.map(users =>
                users.idControl === item.idControl ? { ...users, isBlocked: detail.checked ? 0 : 1 } : users
            ) || []
        );
        const success = await handleUpdateUsers(item.idControl, detail.checked);
        if (!success) {
            setUsers(prevAccesses =>
                prevAccesses?.map(users =>
                    users.idControl === item.idControl ? { ...users, isBlocked: item.isBlocked } : users
                ) || []
            );
        } else {

            setMessagesUsers([
                {
                    type: !detail.checked ? "info" : "warning",
                    content: (<span>
                        Acceso <strong>{item.displayName}</strong> ha sido {detail.checked ? 'bloqueado' : 'desbloqueado'}
                    </span>),
                    dismissible: true,
                    onDismiss: () => setMessagesAccess([]),
                    id: 'access-error',
                },
            ]);
            setTimeout(() => {
                setMessagesUsers([]);
            }, 5000);
        }
    };

    const handleUpdateAccess = async (idAcceso: number, checked: boolean): Promise<boolean> => {
        try {
            setLoading(true)
            const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/admin/accesses/lock',
                    'JWT': JWT,
                },
                body: JSON.stringify({ idAcceso: idAcceso, isActive: checked ? 0 : 1 })
            });
            if (!response.ok) {
                console.error(`HTTP error: ${response.status}`);
                return false; // Indicate failure
            }
            const data = await response.json();
            storeAdmin(data)
            return true; // Indicate success
        } catch (error) {
            console.error('Error updating unit block status:', error);
            return false; // Indicate failure
        } finally {
            setLoading(false)
        }
    };
    const handleUpdateUnits = async (idUnidad: number, checked: boolean): Promise<boolean> => {
        try {
            setLoading(true)
            const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/admin/units/lock',
                    'JWT': JWT,
                },
                body: JSON.stringify({ idUnidad: idUnidad, isBlockUnit: checked ? 1 : 0 })
            });
            if (!response.ok) {
                console.error(`HTTP error: ${response.status}`);
                return false; // Indicate failure
            }
            const data = await response.json();
            storeAdmin(data)
            setLoading(false)
            return true; // Indicate success
        } catch (error) {
            console.error('Error updating unit block status:', error);
            setLoading(false)
            return false; // Indicate failure
        } finally {
            setLoading(false)
        }
    };


    const handleUpdateUsers = async (idControl: number, checked: boolean): Promise<boolean> => {
        try {
            setLoading(true)
            const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/admin/users/lock',
                    'JWT': JWT,
                },
                body: JSON.stringify({ idControl: idControl, isBlocked: checked ? 1 : 0 })
            });
            if (!response.ok) {
                console.error(`HTTP error: ${response.status}`);
                return false; // Indicate failure
            }
            const data = await response.json();
            storeAdmin(data)
            data && data.users ? setUsers(data.users) : setUsers([]);
            return true; // Indicate success
        } catch (error) {
            console.error('Error updating unit block status:', error);
            setLoading(false)
            return false; // Indicate failure
        } finally {
            setLoading(false)
        }
    };


    const handleRefreshUnits = async () => {

        //setLoadingUnits(true);
        try {
            setLoading(true)
            const response = await fetch('https://api2.llavemovil.com/routing/v1/default.aspx', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-routing': '/admin/properties/blocks',
                    'JWT': JWT,
                },
                body: JSON.stringify({})
            });
            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }
            const data = await response.json();
            storeAdmin(data);
            (data.accesses ? setAccesses(data.accesses) : setAccesses([]));
            (data.units && data.units ? setUnits(data.units) : setUnits([]));
            (data.users && data.users ? setUsers(data.users) : setUsers([]));
        } catch (error) {
            console.error('Error fetching units:', error);
        } finally {
            //setLoadingUnits(false);
            setLoading(false)
        }
    };

    useEffect(() => {
    }, [users]); // Add JWT and idPropiedad as dependencies

    const filteredUsers = users.filter(unit => {
        const searchWords = filteringTextUsers.toLowerCase().split(/\s+/).filter(Boolean);
        return searchWords.every(word =>
            unit.displayName.toLowerCase().includes(word) ||
            unit.idControl.toString().toLowerCase().includes(word)
        );
    });

    const paginatedUsers = filteredUsers.slice(
        currentPageIndexUnit * pageSizeUnit,
        (currentPageIndexUnit + 1) * pageSizeUnit
    );

    const filteredUnits = units.filter(unit => {
        const searchWords = filteringTextUnit.toLowerCase().split(/\s+/).filter(Boolean);
        return searchWords.every(word =>
            unit.unidad.toLowerCase().includes(word) ||
            unit.idUnidad.toString().toLowerCase().includes(word)
        );
    });

    const paginatedUnits = filteredUnits.slice(
        currentPageIndexUnit * pageSizeUnit,
        (currentPageIndexUnit + 1) * pageSizeUnit
    );

    const filteredAccesses = accesses.filter(access => {
        const searchWords = filteringTextUnit.toLowerCase().split(/\s+/).filter(Boolean);
        return searchWords.every(word =>
            access.acceso.toLowerCase().includes(word) ||
            access.idAcceso.toString().toLowerCase().includes(word)
        );
    });

    const paginatedAccesses = filteredAccesses.slice(
        currentPageIndexAccess * pageSizeAccess,
        (currentPageIndexAccess + 1) * pageSizeAccess
    );

    return (
        <CustomAppLayout
            breadcrumbs={
                <BreadcrumbGroup
                    items={[
                        { text: 'Inicio', href: '#home' },
                        { text: 'Seguridad', href: '#security' },
                        { text: 'Bloqueos', href: '#create-transcription-job' },
                    ]}
                    expandAriaLabel="Show path"
                    ariaLabel="Breadcrumbs"
                />
            }
            content={
                <Box padding="s">
                    <h1>{propiedad} ({idPropiedad})</h1>
                    <Tabs
                        activeTabId={activeTabId}
                        onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                        tabs={[
                            {
                                label: "Unidades",
                                id: "unit",
                                content: (
                                    <SpaceBetween direction='vertical' size='xs'>
                                        <Flashbar items={messagesUnit}></Flashbar>
                                        <Table
                                            stripedRows
                                            columnDefinitions={[
                                                {
                                                    id: "unidad",
                                                    header: "Unidad",
                                                    cell: (item) => item.unidad,
                                                    isRowHeader: true,
                                                },
                                                {
                                                    id: "users",
                                                    header: "Bloqueo",
                                                    width: 120,
                                                    cell: (item) => (
                                                        <Toggle
                                                            onChange={({ detail }) => handleChangeUnit(item, detail)}
                                                            checked={item.isBlockUnit === 1}
                                                        />
                                                    ),
                                                },
                                            ]}
                                            items={paginatedUnits}
                                            trackBy="idUnidad"
                                            empty={
                                                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                                    <SpaceBetween size="m">
                                                        <b>No tiene unidades registradas</b>
                                                    </SpaceBetween>
                                                </Box>
                                            }
                                            filter={
                                                <TextFilter
                                                    filteringText={filteringTextUnit}
                                                    onChange={({ detail }) => {
                                                        setFilteringTextUnit(detail.filteringText);
                                                        setCurrentPageIndexUnit(0); // Reset page on filter
                                                    }}
                                                />
                                            }
                                            pagination={
                                                filteredUnits.length > pageSizeUnit && (
                                                    <Pagination
                                                        currentPageIndex={currentPageIndexUnit}
                                                        pagesCount={Math.ceil(filteredUnits.length / pageSizeUnit)}
                                                        onChange={({ detail }) => setCurrentPageIndexUnit(detail.currentPageIndex)}
                                                    />
                                                )
                                            }
                                        />
                                    </SpaceBetween>
                                ),
                            },
                            {
                                label: "Accesos",
                                id: "access",
                                content: (

                                    <SpaceBetween direction='vertical' size='xs'>
                                        <Flashbar items={messagesAccess}></Flashbar>
                                        <Table
                                            stripedRows
                                            columnDefinitions={[
                                                {
                                                    id: "acceso",
                                                    header: "Acceso",
                                                    cell: (item) => (
                                                        <span style={{ fontWeight: item.isActive === 0 ? "bold" : "normal" }}>
                                                            {item.sector !== '*' ? <Badge color='blue'>{item.sector}</Badge> : null}{item.sector !== '*' ? " " : ""}{item.acceso}

                                                        </span>
                                                    ),
                                                    isRowHeader: true,
                                                },
                                                {
                                                    id: "users",
                                                    header: "Bloqueo",
                                                    width: 120,
                                                    cell: (item) => (
                                                        <Toggle
                                                            onChange={({ detail }) => handleChangeAccess(item, detail)}
                                                            checked={item.isActive === 0}
                                                        />
                                                    ),
                                                },
                                            ]}
                                            items={paginatedAccesses}
                                            trackBy="idAcceso"
                                            empty={
                                                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                                    <SpaceBetween size="m">
                                                        <b>No tiene accesos registrados</b>
                                                    </SpaceBetween>
                                                </Box>
                                            }
                                            filter={
                                                <TextFilter
                                                    filteringText={filteringTextAccess}
                                                    onChange={({ detail }) => {
                                                        setFilteringTextAccess(detail.filteringText);
                                                        setCurrentPageIndexAccess(0); // Reset page on filter
                                                    }}
                                                />
                                            }
                                            pagination={
                                                filteredAccesses.length > pageSizeAccess && (
                                                    <Pagination
                                                        currentPageIndex={currentPageIndexAccess}
                                                        pagesCount={Math.ceil(filteredAccesses.length / pageSizeAccess)}
                                                        onChange={({ detail }) => setCurrentPageIndexAccess(detail.currentPageIndex)}
                                                    />
                                                )
                                            }
                                        />
                                    </SpaceBetween>
                                ),
                            },
                            {
                                label: "Usuarios",
                                id: "user",
                                content: (
                                    <SpaceBetween direction='vertical' size='xs'>
                                        <Flashbar items={messagesUsers}></Flashbar>
                                        <Table
                                            stripedRows
                                            columnDefinitions={[
                                                {
                                                    id: "usuario",
                                                    header: "Usuario",
                                                    cell: (item) => item.displayName,
                                                    isRowHeader: true,
                                                },
                                                {
                                                    id: "movil",
                                                    header: "Móvil",
                                                    cell: (item) => formatPhone(item.movil),
                                                    isRowHeader: true,
                                                },
                                                {
                                                    id: "email",
                                                    header: "Email",
                                                    cell: (item) => item.email,
                                                    isRowHeader: true,
                                                },
                                                {
                                                    id: "users",
                                                    header: "Bloqueo",
                                                    width: 80,
                                                    cell: (item) => (
                                                        <Toggle
                                                            onChange={({ detail }) => handleChangeUsers(item, detail)}
                                                            checked={item.isBlocked === 1}
                                                        />
                                                    ),
                                                },
                                            ]}
                                            items={paginatedUsers}
                                            trackBy="idControl"
                                            empty={
                                                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                                                    <SpaceBetween size="m">
                                                        <b>No tiene accesos registrados</b>
                                                    </SpaceBetween>
                                                </Box>
                                            }
                                            filter={
                                                <TextFilter
                                                    filteringText={filteringTextUsers}
                                                    onChange={({ detail }) => {
                                                        setFilteringTextUsers(detail.filteringText);
                                                        setCurrentPageIndexUsers(0); // Reset page on filter
                                                    }}
                                                />
                                            }
                                            pagination={
                                                filteredUsers.length > pageSizeUsers && (
                                                    <Pagination
                                                        currentPageIndex={currentPageIndexUsers}
                                                        pagesCount={Math.ceil(filteredUsers.length / pageSizeUsers)}
                                                        onChange={({ detail }) => setCurrentPageIndexAccess(detail.currentPageIndex)}
                                                    />
                                                )
                                            }
                                        />
                                    </SpaceBetween>
                                ),
                            },
                        ]}
                    />
                </Box>
            }
        />
    );
};